import { EventEmitter } from "./event-emitter";

const HEADER_LENGTH = 7;
const CHUNK_SIZE = 20;
const DEFAULT_MASTER_KEY = new TextEncoder().encode("ACR1255U-J1 Auth");

enum Commands {
	POWER_ON = 0x62,
	POWER_OFF = 0x63,
	SLOT_STATUS = 0x65,
	ESCAPE = 0x6B,
	APDU = 0x6F,
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
enum Responses {
	DATA_BLOCK = 0x80,
	SLOT_STATUS = 0x81,
	ESCAPE = 0x83,
	NOTIFY_CARD_STATUS = 0x50,
	HARDWARE_ERROR = 0x51
}


function zeroPadToABy16DivisibleLength(data:Uint8Array):Uint8Array{
	if (data.length % 16 == 0) return data;
	let targetLength = Math.floor(data.length / 16) + 16;
	let result = new Uint8Array(targetLength);
	result.set(data);
	return result;
}

function concatUint8Arrays(...arrays:Uint8Array[]):Uint8Array{
	let totalLength = 0;
	for (let arr of arrays){
		totalLength += arr.length;
	}
	let result = new Uint8Array(totalLength);
	let offset = 0;
	for (let arr of arrays){
		result.set(arr, offset);
		offset += arr.length;
	}
	return result;
}

function uint8ArrayToHexArray(arr:Uint8Array):string[]{
	return Array.from(arr).map(a=>a.toString(16).padStart(2,"0"));
}

const ZERO_INITIALIZATION_VECTOR = new Uint8Array(16);
async function encrypt(key:CryptoKey, data:Uint8Array):Promise<Uint8Array>{
	let result = await crypto.subtle.encrypt({name: "AES-CBC", iv: ZERO_INITIALIZATION_VECTOR}, key, data as any as BufferSource);
	return new Uint8Array(result).slice(0, data.length); // Drop the padding appended by bcrypt
}

async function decrypt(key:CryptoKey, data:Uint8Array){
	 // Create a valid padding block
	let last = data.slice(-16);
	let pad = new Uint8Array(await crypto.subtle.encrypt({name: "AES-CBC", iv: last}, key, new Uint8Array(0)));

	return new Uint8Array(await crypto.subtle.decrypt({name: "AES-CBC", iv: ZERO_INITIALIZATION_VECTOR}, key, concatUint8Arrays(data, pad) as any as BufferSource));
}

async function uint8KeyToCryptoKey(key:Uint8Array):Promise<CryptoKey> {
	return crypto.subtle.importKey("raw", key as any as BufferSource, {name: "AES-CBC"}, false, ["encrypt", "decrypt"]);
}

function areUint8ArraysEqual(arr1:Uint8Array, arr2:Uint8Array):boolean {
	if (arr1.length != arr2.length) return false;
	for (let i = 0; i < arr1.length; i++){
		if (arr1[i] != arr2[i]){
			return false;
		}
	}
	return true;
}

class Arc1255BluetoothNfc extends EventEmitter {
	public readonly READER_SCANNED_EVENT = "nfc-scanned";
	private tx?:BluetoothRemoteGATTCharacteristic;
	private rx?:BluetoothRemoteGATTCharacteristic;
	private pending?:((result:Uint8Array)=>void);
	private receiveBuffer:Uint8Array = new Uint8Array();
	private masterKey?:CryptoKey;
	private sessionKey?:CryptoKey;

	public constructor(){
		super();
		this.on("card-status-changed", (present:boolean)=>{
			console.log("Is card present?", present);
		});
		this.on("card-uid", (uid:string)=>{
			console.log("Card scanned", uid);
			this.emit(this.READER_SCANNED_EVENT, uid);
		});
	}

	private buildFrame(frameType:number, data:Uint8Array = new Uint8Array(0), seq = 0, param = 0):Uint8Array {
		let result = new Uint8Array(HEADER_LENGTH + data.length);
		result[0] = frameType;
		result[1] = data.length >> 8;
		result[2] = data.length & 0xff;
		result[3] = 0x00;
		result[4] = seq;
		result[5] = param;
		result.set(data, HEADER_LENGTH);

		// Create checksum
		// XOR of every other byte
		let x = 0;
		for (let i = 0; i < result.length; i++) {
			if (i != 6) {
				x ^= result[i];
			}
		}
		result[6] = x;
		return result;
	}

	private buildPayload(frame:Uint8Array):Uint8Array {
		let result = new Uint8Array(5 + frame.length);
		result[0] = 0x05;
		result[1] = frame.length >> 8;
		result[2] = frame.length & 0xff;
		result.set(frame, 3);
		// Create checksum
		// XOR of every other byte
		let x = 0;
		for (let i = 1; i < result.length - 2; i++) {
			x ^= result[i];
		}
		result[result.length-2] = x;
		result[result.length-1] = 0x0A;
		return result;
	}

	private getDataFromFrame(frame:Uint8Array):Uint8Array {
		return frame.slice(HEADER_LENGTH);
	}

	public async connect(){
		if (!navigator.bluetooth) return;
		let device = await navigator.bluetooth.requestDevice({
			filters: [{
				namePrefix: "ACR"
			}],
			optionalServices: [
				"generic_access",
				"generic_attribute",
				"scan_parameters",
				"3c4afff0-4783-3de5-a983-d348718ef133",
			]
		});
		if (!device) return;
		if (!device.gatt) return;
		this.masterKey = await uint8KeyToCryptoKey(DEFAULT_MASTER_KEY);
		let server = await device.gatt.connect();
		let service = await server.getPrimaryService("3c4afff0-4783-3de5-a983-d348718ef133");
		let characteristics = await service.getCharacteristics();
		this.tx = characteristics.find(c=>c.uuid == "3c4afff1-4783-3de5-a983-d348718ef133") as BluetoothRemoteGATTCharacteristic;
		this.rx = characteristics.find(c=>c.uuid == "3c4afff2-4783-3de5-a983-d348718ef133") as BluetoothRemoteGATTCharacteristic;
		if (!this.tx){
			throw new Error("No transmit characteristic found");
		}
		if (!this.rx) {
			throw new Error("No receive characteristic found");
		}

		this.rx.addEventListener("characteristicvaluechanged", (e)=>{
			if (!e.target) return;
			let v = (e.target as any).value as DataView;
			this.onChunk(new Uint8Array(v.buffer.slice(v.byteOffset, v.byteOffset + v.byteLength)));
		});
		await this.rx.startNotifications();
		await this.authenticate();

		await this.setAutoPolling(true);
		await this.powerOn();
	}

	private onChunk(chunk:Uint8Array) {
		if (this.receiveBuffer.length == 0){
			this.receiveBuffer = chunk;
		}else{
			this.receiveBuffer = concatUint8Arrays(this.receiveBuffer, chunk);
		}

		let dataLength = this.receiveBuffer[1] << 8;
		dataLength += this.receiveBuffer[2];
		if (this.receiveBuffer.length - 5 >= dataLength) {
			let result = this.receiveBuffer;
			this.receiveBuffer = new Uint8Array();
			this.onFrame(result.slice(3, result.length-2));
		}
	}

	private async onFrame(frame:Uint8Array) {
		if (this.sessionKey) {
			try{
				let decrypted = await decrypt(this.sessionKey, frame);
				if (decrypted[0] == Responses.NOTIFY_CARD_STATUS){
					let cardPresent = decrypted[5] == 0x03;
					this.emit("card-status-changed", cardPresent);
					if (cardPresent){
						let uid = await this.getCardUid();
						this.emit("card-uid", uid);
					}
					return;
				}
			}catch(err){
			}
		}
		if (!this.pending) return;
		this.pending(frame);
	}

	private async write(frame:Uint8Array){
		if (!this.tx) throw new Error("Attempting to write with no tx defined");
		let payload = this.buildPayload(frame);
		for (let i = 0; i < payload.length; i+=CHUNK_SIZE){
			let chunk = payload.slice(i, i+CHUNK_SIZE);
			if (this.tx.properties.write){
				await this.tx.writeValueWithResponse(chunk);
			}else{
				await this.tx.writeValueWithoutResponse(chunk);
			}
		}
	}

	private async transceive(frame:Uint8Array):Promise<Uint8Array>{
		let response = new Promise<Uint8Array>((resolve, reject) =>{
			let timer = setTimeout(()=>{
				this.pending = undefined;
				reject(new Error("timeout"));
			}, 3000);
			this.pending = (result:Uint8Array)=>{
				clearTimeout(timer);
				this.pending = undefined;
				resolve(result);
			};
		});

		await this.write(frame);
		return response;
	}

	private async secureTransceive(frame:Uint8Array):Promise<Uint8Array>{
		if (!this.sessionKey) throw new Error("Attempting to do a secure transceive before a session has been made");
		let encrypted = await encrypt(this.sessionKey, zeroPadToABy16DivisibleLength(frame));
		let result = await this.transceive(encrypted);
		return decrypt(this.sessionKey, result);
	}

	private async authenticate(){
		if (!this.masterKey) throw new Error("No master key set");
		let result = await this.transceive(this.buildFrame(Commands.ESCAPE, Uint8Array.of(0xe0, 0x00,0x00, 0x45, 0x00)));
		let responseType = result[0];
		if (responseType != Responses.ESCAPE) {
			throw new Error(`Got invalid response for authentication request. Response type is ${responseType.toString(16)}. Expected 0x83\n` + uint8ArrayToHexArray(result));
		}
		let data = this.getDataFromFrame(result);
		let randomBytes1 = await decrypt(this.masterKey, data.slice(5));
		let randomBytes2 = crypto.getRandomValues(new Uint8Array(16));
		let authData = await decrypt(this.masterKey, concatUint8Arrays(randomBytes2, randomBytes1));
		result = await this.transceive(this.buildFrame(Commands.ESCAPE, concatUint8Arrays(Uint8Array.of(0xe0, 0x00,0x00, 0x46, 0x00), authData)));
		responseType = result[0];
		if (responseType != Responses.ESCAPE) {
			throw new Error(`Got invalid response for authentication response. Response type is ${responseType.toString(16)}. Expected 0x83\n` + uint8ArrayToHexArray(result));
		}
		data = this.getDataFromFrame(result);
		let responseBytes2 = await decrypt(this.masterKey, data.slice(5));
		if (!areUint8ArraysEqual(randomBytes2, responseBytes2)){
			throw new Error(`Handshake error. Response bytes don't match`);
		}
		this.sessionKey = await uint8KeyToCryptoKey(concatUint8Arrays(randomBytes1.slice(0,8), randomBytes2.slice(0,8)));
	}

	public async powerOn(){
		let result = await this.secureTransceive(this.buildFrame(Commands.POWER_ON));
		if (result[0] != Responses.DATA_BLOCK) {
			throw new Error("Power on command gave an unexpected response"+ uint8ArrayToHexArray(result));
		}
	}

	public async powerOff(){
		let result = await this.secureTransceive(this.buildFrame(Commands.POWER_OFF));
		if (result[0] != Responses.SLOT_STATUS) {
			throw new Error("Power off command gave an unexpected response"+ uint8ArrayToHexArray(result));
		}
	}

	public async getSlotStatus():Promise<{cardPresent:boolean, cardActive:boolean}>{
		let result = await this.secureTransceive(this.buildFrame(Commands.SLOT_STATUS));
		if (result[0] != Responses.SLOT_STATUS) {
			throw new Error("Get slot status command gave an unexpected response "+ uint8ArrayToHexArray(result));
		}
		let data = result[5];
		let info = {
			cardPresent: (data & 2) != 2,
			cardActive: (data & 1) != 1
		};
		if (!info.cardPresent){
			info.cardActive = false;
		}
		return info;
	}

	public async setAutoPolling(on:boolean){
		let result = await this.secureTransceive(this.buildFrame(Commands.ESCAPE, Uint8Array.of(0xe0, 0,0, 0x40, on ? 1 : 0)));
		if (result[0] != Responses.ESCAPE) {
			throw new Error("set auto polling command gave an unexpected response "+ uint8ArrayToHexArray(result));
		}
	}

	public async getCardUid():Promise<string> {
		let result = await this.secureTransceive(this.buildFrame(Commands.APDU, Uint8Array.of(0xff, 0xca, 0x00,0x00,0x00)));
		if (result[0] != Responses.DATA_BLOCK){
			throw new Error("get card uid command gave an unexpected response " + uint8ArrayToHexArray(result));
		}
		let uid = uint8ArrayToHexArray(result.slice(7,11)).join("");
		return uid;
	}

	public canConnect():boolean{
		return navigator.bluetooth != undefined;
	}
}

export const Arc1255BluetoothNfcReader = new Arc1255BluetoothNfc();
